/**
 * IV sedation dentistry cluster — final patient-facing copy.
 *
 * One hub + three treatment pages. Conservative clinical wording by instruction:
 * no sedative drug named, no fasting rules, no monitoring protocol, no outcome
 * promises. The escort requirement and "no driving for the rest of the day" are
 * stated plainly because they decide whether a patient can attend at all.
 *
 * Fee figures are "from" placeholders signed off for launch; every patient gets a
 * fixed written quote after an in-person assessment.
 */
import { areas } from './areas';

export const SEDATION_FEES = {
  assessment: 'from £75',
  firstHour: 'from £450',
  extraHour: 'from £250',
};

/** The ten areas already published elsewhere on the site, with their existing journey times. */
export const SEDATION_AREAS = areas.map((a) => ({
  slug: a.slug,
  name: a.name,
  postcode: a.postcode,
  minutes: a.distanceMinutes,
  station: a.tubes[0],
}));

/** Footer group + Services navigation. */
export const SEDATION_NAV = [
  { to: '/iv-sedation-dentistry', label: 'IV Sedation Dentistry' },
  { to: '/iv-sedation-wisdom-teeth-removal', label: 'Wisdom Teeth Under Sedation' },
  { to: '/iv-sedation-dental-implants', label: 'Implants Under Sedation' },
  { to: '/iv-sedation-dental-phobia', label: 'Dental Phobia Help' },
];

/** Suitability enquiry options. Deliberately not a quiz — there is no scoring and no verdict. */
export const SUITABILITY = {
  treatments: [
    'Wisdom teeth removal',
    'A difficult or surgical extraction',
    'Dental implants',
    'Several fillings or crowns in one visit',
    'A check-up and clean I cannot face',
    'Not sure yet',
  ],
  anxiety: [
    'Uneasy, but I manage',
    'Very anxious — I dread it for days',
    'I have avoided the dentist for years',
    'I have walked out, or cannot get through an appointment',
  ],
  flags: [
    'Pregnant or breastfeeding',
    'Heart or lung condition',
    'I take regular medication',
    'Previous reaction to sedation',
    'Under 18',
    'None of these',
  ],
  escort: [
    'Yes — someone can bring me and take me home',
    'Probably, I need to arrange it',
    'No, or I am not sure',
  ],
};

/** Shared logistics. Specific to sedation: you cannot drive yourself home. */
export const GETTING_HOME = [
  'Leave the car at home. You will not be able to drive it back, and it cannot sit on a pay-and-display bay all day.',
  'Bring an adult escort, or agree a collection time with them. If they would rather not wait, we call them when you are close to ready.',
  'By train: Acton Main Line (Elizabeth line and GWR) is a four-minute walk, Acton Central (Overground) about nine. Both are easy places for someone to meet you.',
  'By taxi: there is room to pull in on Horn Lane directly outside, which is the simplest way home if you live a short ride away.',
  'Your escort is welcome to wait inside for the whole appointment, or use the cafés and the Morrisons three minutes up Horn Lane. Pay-and-display parking is on Horn Lane and the surrounding streets.',
  'Please do not take a long tube journey or a bus home on your own, even if you feel completely fine when you leave.',
];

const SEDATIONIST = 'Dr Kartik Patel BDS, GDC 177993';

const feeRows = (treatmentRow) => [
  ['Sedation assessment', SEDATION_FEES.assessment, 'In person, before any sedation appointment is booked'],
  ['IV sedation — first hour', SEDATION_FEES.firstHour, 'Sedationist, monitoring and monitored recovery time'],
  ['Each additional hour', SEDATION_FEES.extraHour, 'Agreed in the written quote, not added on the day'],
  treatmentRow,
];

export const sedationPages = {
  // ==========================================================================
  // HUB
  // ==========================================================================
  hub: {
    key: 'hub',
    path: '/iv-sedation-dentistry',
    eyebrow: 'Acton W3 · serving West London',
    h1: 'IV Sedation Dentistry in Acton, West London',
    title: 'IV Sedation Dentist Acton W3 | Restore.Dental',
    description:
      'IV sedation dentistry in Acton W3. Awake but deeply relaxed, usually little memory of the appointment. From £450. Adult escort required. Assessment first.',
    keywords: [
      'IV sedation dentist near me',
      'sedation dentist Acton',
      'sedation dentistry West London',
      'IV sedation dentistry W3',
      'asleep dentist London',
      'conscious sedation dentist Ealing',
    ],
    image: {
      src: 'https://images.unsplash.com/photo-1465409042654-5314e9d1754b?crop=entropy&cs=srgb&fm=jpg&q=80&w=900',
      alt: 'A patient sitting calmly with her eyes closed',
    },
    directAnswer: {
      question: 'What is IV sedation dentistry, and what does it cost in Acton?',
      answer:
        'IV sedation is a sedative given through a small cannula in your hand or arm. You stay awake and able to respond, but deeply relaxed, and most people remember little of the appointment. We provide it at our Acton W3 practice from £450. An adult escort is required, and suitability is decided at an in-person assessment.',
      bullets: [
        'Sedation from £450 for the first hour',
        `Given by ${SEDATIONIST}`,
        'Adult escort required — no driving that day',
        'Assessment first, then a fixed written quote',
      ],
    },
    feels: {
      heading: 'What IV sedation actually feels like',
      intro:
        'Two things stop most people asking about sedation: the fear of not being in control, and the fear of remembering it anyway. Here is the honest version of both.',
      points: [
        {
          title: 'You are not unconscious',
          body: 'This is not a general anaesthetic. You stay awake, breathing for yourself, and you can answer if someone speaks to you. What changes is that you stop minding that you are at the dentist.',
        },
        {
          title: 'Drowsy, warm, uninterested',
          body: 'Most people describe a heavy, pleasantly drowsy feeling that arrives within a minute or two. The sounds and sensations are still there — they simply stop mattering, and time folds up on itself.',
        },
        {
          title: 'Usually little or no memory',
          body: 'Most patients remember arriving, and then very little until they are in recovery. We say "most" on purpose: memory varies from person to person, and we are not going to promise you a blank.',
        },
        {
          title: 'You stay in charge',
          body: 'You can raise a hand at any point and everything stops. Nothing is done that you have not agreed to beforehand, and the sedation is adjusted as the appointment goes along.',
        },
      ],
      reassure:
        'If you would rather talk this through before committing to anything at all, call us. You can ask every question you have on the phone without booking a thing.',
    },
    oneAppointment: {
      heading: 'What we can get done in one appointment',
      intro:
        'The practical argument for sedation is not comfort alone — it is compression. Treatment normally spread over five or six visits can often be done in one longer appointment while you are relaxed.',
      items: [
        'Several fillings in one sitting, instead of one tooth per visit',
        'A difficult or surgical extraction, including wisdom teeth',
        'Implant placement, with or without grafting',
        'Deep cleaning and gum treatment you could not otherwise tolerate',
        'A check-up, x-rays and the first stage of treatment together, for patients who have stayed away for years',
      ],
      note:
        'How much we can do in one go depends on your mouth, your medical history and how long you can safely be sedated. We plan the appointment together at the assessment, so you know what is being done and what it costs before the day.',
    },
    suits: {
      heading: "Who it suits, and when it isn't the right option",
      yes: [
        'Patients who are frightened of dental treatment, including people who have avoided it for years',
        'Needle phobia, or a gag reflex that makes ordinary treatment impossible',
        'Surgery you would rather not be aware of — wisdom teeth, extractions, implants',
        'Long appointments, or several things that need doing at once',
        'Patients who find it hard to sit still, or to keep their mouth open for long',
      ],
      caution: [
        'Some medical and medication histories mean sedation is not the safer choice',
        'Pregnancy and breastfeeding need separate advice',
        'We do not sedate under-18s',
        'Sometimes local anaesthetic alone, with more time and a gentler approach, is genuinely the better answer',
        'Where sedation is not suitable we discuss the alternatives, or refer you to a setting that can help',
      ],
      note:
        'Suitability is never decided from a web form or a phone call. It is decided at an in-person assessment, where we go through your medical history, examine you, and talk about what is actually involved.',
    },
    day: {
      heading: 'Your sedation day, step by step',
      summary: 'Read the whole thing, or skip it — it is here for the people who want the full picture.',
      steps: [
        {
          title: 'Before the day — the assessment',
          body: 'A separate, shorter appointment. We examine you, go through your medical history and medication, explain what sedation will and will not do, plan the treatment and give you a written quote. Then you go home and decide.',
        },
        {
          title: 'Bring a responsible adult',
          body: 'Someone over 18 has to come with you, or meet you here, and take you home afterwards. We cannot sedate you if you arrive alone — it is the one rule we cannot bend, so it is worth arranging early.',
        },
        {
          title: 'Settling in',
          body: 'You sit down, we check you over and answer anything you are still worried about. Nothing starts until you say you are ready.',
        },
        {
          title: 'A small cannula',
          body: 'A fine cannula goes into the back of your hand or your arm. If needles are the thing you dread, say so — this is the part we take the most care over, and there is more than one way to make it easier.',
        },
        {
          title: 'Treatment, monitored throughout',
          body: 'The sedative goes in through the cannula and takes effect within a minute or two. You are monitored continuously for the whole appointment while the treatment is carried out as planned.',
        },
        {
          title: 'Recovery, 20 to 30 minutes',
          body: 'You rest with us until you are steady on your feet and we are happy to let you go. Most people are ready after twenty to thirty minutes, and then your escort takes you home.',
        },
        {
          title: 'The rest of the day',
          body: 'No driving, no work, no important decisions and no looking after small children on your own. Plan a quiet day. Most people feel like themselves again the next morning.',
        },
      ],
    },
    safety: {
      heading: 'Who gives the sedation, and how you are kept safe',
      summary: 'The section nobody writes plainly. Open it if being safe matters more to you than being reassured.',
      points: [
        {
          title: 'A qualified sedationist',
          body: `Your sedation is given by ${SEDATIONIST}, qualified in intravenous conscious sedation, with the practice team trained to assist and monitor you throughout.`,
        },
        {
          title: 'Continuous monitoring',
          body: 'Your oxygen levels, pulse and blood pressure are monitored for the whole appointment by a trained team member whose job during that time is you.',
        },
        {
          title: 'Emergency drugs and protocols',
          body: 'The drugs and equipment needed to reverse sedation and to manage an emergency are kept on site, checked, and the team rehearses using them.',
        },
        {
          title: 'Screened in advance, not on the day',
          body: 'Your medical history, your medication and any previous reaction to sedation are reviewed at the assessment beforehand, so nothing is a surprise when you arrive.',
        },
        {
          title: 'Registered and regulated',
          body: 'Every clinician here is registered with the General Dental Council, and you are welcome to check the register yourself before you come.',
        },
      ],
    },
    fees: {
      heading: 'What IV sedation costs',
      intro:
        'Sedation is charged for the sedation itself, on top of the treatment being carried out. Every patient gets one fixed written quote covering the whole appointment after the assessment — never a guess over the phone.',
      rows: feeRows(['The treatment itself', 'quoted separately', 'See the full fee guide for treatment prices']),
    },
    local: {
      heading: 'IV sedation for patients across West London',
      intro:
        'We are one practice, at 64a Horn Lane in Acton W3 — there is no branch network, which is part of why patients travel to us. Sedation dentistry is thinly spread across West London, and most of the people we sedate come from a few stops away.',
    },
    gettingHome: {
      heading: 'Getting here, and getting home afterwards',
      intro:
        'This matters more for a sedation appointment than for any other, because you cannot drive yourself home and you should not travel home alone.',
    },
    related: [
      { to: '/iv-sedation-wisdom-teeth-removal', label: 'Wisdom teeth under sedation', body: 'All four in one appointment, or one difficult lower tooth.' },
      { to: '/iv-sedation-dental-implants', label: 'Implants under sedation', body: 'Planned surgery, which makes it one of the best fits for sedation.' },
      { to: '/iv-sedation-dental-phobia', label: 'Frightened of the dentist?', body: 'Where to start if you have been avoiding this for years.' },
      { to: '/services/sedation', label: 'Sedation options explained', body: 'How IV sedation compares with the alternatives.' },
      { to: '/implants-acton', label: 'Dental implants in Acton', body: 'Costs, timings and what implant treatment involves.' },
      { to: '/emergency-dentist-acton', label: 'Emergency dentist Acton', body: 'In pain today? Start here — sedation can follow later.' },
      { to: '/knowledge-base/a-guide-to-what-iv-sedation-feels-like', label: 'Guide: what sedation feels like', body: 'Minute by minute, from the cannula to getting home.' },
      { to: '/knowledge-base/a-guide-to-iv-sedation-vs-general-anaesthetic', label: 'Guide: sedation vs general anaesthetic', body: '"Can I just be put to sleep?" — the honest difference.' },
      { to: '/knowledge-base/a-guide-to-what-iv-sedation-really-costs', label: 'Guide: what sedation really costs', body: 'How the bill is built, and what to ask before you book.' },
    ],
    enquiryIntro:
      'Six quick questions. It is not a screening test and it will not tell you whether you are suitable — it just means whoever calls you already knows the important parts.',
    faqs: [
      {
        question: 'How much does IV sedation cost in Acton?',
        answer:
          'Sedation starts from £450 for the first hour and from £250 for each additional hour, on top of the cost of the dental treatment itself. A sedation assessment is from £75. After that assessment you get one fixed written quote covering everything, and 0% finance is available over £500.',
      },
      {
        question: 'Is IV sedation safe?',
        answer:
          'Intravenous conscious sedation has a long safety record when it is properly assessed, given and monitored. Your medical history is reviewed in advance, your oxygen, pulse and blood pressure are watched throughout, and reversal drugs are on site. No sedation is risk free, which is exactly why some patients are advised against it.',
      },
      {
        question: 'Will I remember the appointment?',
        answer:
          'Most people remember arriving, and then very little until recovery. Some remember fragments and a few remember more than they expected. We will not promise you a complete blank. What we can say is that the memory is usually hazy, and far less distressing than being fully aware.',
      },
      {
        question: 'Can I drive after IV sedation?',
        answer:
          'No. You cannot drive for the rest of the day, and you should not cycle, use machinery or sign anything important either. Leave the car at home. Most people are safe to drive again the following morning, once a full night of sleep has passed.',
      },
      {
        question: 'Do I need someone to come with me?',
        answer:
          'Yes. A responsible adult over 18 must bring you or meet you here, take you home, and ideally stay with you for the evening. We cannot go ahead if you arrive alone. If arranging an escort is difficult, tell us early and we will work around the timing.',
      },
      {
        question: 'How long does IV sedation last?',
        answer:
          'The sedative takes effect in a minute or two, and we keep you comfortable for as long as the treatment needs. Recovery with us is usually twenty to thirty minutes. Drowsiness and poor judgement can linger for the rest of the day, which is why the escort rule exists.',
      },
      {
        question: 'Is IV sedation the same as being put to sleep?',
        answer:
          'No. A general anaesthetic makes you fully unconscious and is done in hospital. IV sedation leaves you awake, breathing for yourself and able to respond, but deeply relaxed and uninterested in what is happening. It is sometimes called twilight sedation for that reason.',
      },
      {
        question: 'What if I take regular medication?',
        answer:
          'Bring a list of everything you take, including inhalers, supplements and anything bought over the counter. Some medicines interact with sedation and some conditions change the plan. That is what the assessment is for — nothing is decided until we have seen your full history.',
      },
    ],
  },

  // ==========================================================================
  // WISDOM TEETH
  // ==========================================================================
  wisdom: {
    key: 'wisdom',
    path: '/iv-sedation-wisdom-teeth-removal',
    eyebrow: 'Acton W3 · surgical extractions under sedation',
    h1: 'Wisdom Teeth Removal Under IV Sedation — Acton',
    title: 'Wisdom Teeth Removal Under Sedation | Acton W3',
    description:
      'Wisdom tooth removal under IV sedation in Acton W3. Awake but deeply relaxed, usually little memory. Sedation from £450 plus the extraction. Escort required.',
    keywords: [
      'wisdom tooth removal under sedation',
      'asleep for wisdom teeth',
      'wisdom teeth removal sedation London',
      'sedation wisdom teeth Acton',
      'surgical extraction under sedation West London',
    ],
    image: {
      src: 'https://images.unsplash.com/photo-1769050612649-d1f453b42036?crop=entropy&cs=srgb&fm=jpg&q=80&w=900',
      alt: 'An armchair in quiet afternoon light — a day set aside to recover',
    },
    directAnswer: {
      question: 'Can I have my wisdom teeth out under sedation?',
      answer:
        'Yes. At our Acton W3 practice your wisdom teeth can be taken out under IV sedation — awake and breathing for yourself, but deeply relaxed and usually remembering little. Sedation starts from £450 on top of the extraction fee, an adult escort is required, and we assess you in person first.',
      bullets: [
        'All four in one appointment where that is the plan',
        'Sedation from £450, plus the extraction fee',
        `Given by ${SEDATIONIST}`,
        'Adult escort required — plan a quiet day after',
      ],
    },
    feels: {
      heading: 'What it feels like to have wisdom teeth out under sedation',
      intro:
        'For most people the part they dread is not the pain — it is lying there, fully aware, listening to it happen. That is the part sedation changes.',
      points: [
        {
          title: 'You will not be following along',
          body: 'Awake, yes. Paying attention, no. The pushing, the sounds and the sheer length of a surgical extraction are the things patients remember most, and those are precisely the things sedation blunts.',
        },
        {
          title: 'Local anaesthetic still does the numbing',
          body: 'Sedation relaxes you, it does not numb you. Local anaesthetic is still used, and it is usually given once the sedation has taken effect — which is the bit needle-shy patients care about most.',
        },
        {
          title: 'It passes faster than you expect',
          body: 'A straightforward wisdom tooth can be minutes; a buried lower one takes considerably longer. Either way, sedated patients routinely say afterwards that it felt like no time at all.',
        },
        {
          title: 'Usually little memory of the surgery',
          body: 'Most people remember the cannula, then recovery. We will not promise a blank, but the distressing middle section is the part most likely to be missing when you look back.',
        },
      ],
      reassure:
        'If you have been carrying a referral letter around for months, a phone call costs nothing and commits you to nothing.',
    },
    oneAppointment: {
      heading: 'All four in one go, or one at a time?',
      intro: 'Both are possible, and sedation is what makes the first option realistic for most people.',
      items: [
        'All four wisdom teeth removed in a single sedated appointment — one recovery, one stretch of time off',
        'Two on the same side, so you always have a comfortable side to eat on',
        'One difficult lower wisdom tooth where the surgery is expected to take a while',
        'Wisdom teeth combined with other treatment you have been putting off, in the same appointment',
        'Surgical removal of a tooth that is buried, tilted, or close to the nerve',
      ],
      note:
        'Which option is right depends on the x-rays, the shape and position of the roots, and how much time you can take off. It is discussed at the assessment and agreed before the sedation appointment is booked.',
    },
    suits: {
      heading: 'When sedation is the right call for wisdom teeth — and when it is not',
      yes: [
        'Buried, impacted or tilted wisdom teeth, where surgery is expected',
        'Repeated infections, swelling or pain around a wisdom tooth',
        'Patients who could not face a long extraction while fully aware',
        'A strong gag reflex, or a jaw that aches from being held open',
        'Anyone who would rather have all four done at once than attend four appointments',
      ],
      caution: [
        'A simple, fully erupted wisdom tooth often comes out comfortably with local anaesthetic alone',
        'Some medical and medication histories mean sedation is not the safer choice',
        'Cases lying very close to the nerve may be better handled by a hospital oral surgery department',
        'We do not sedate under-18s',
        'If a wisdom tooth is causing no trouble at all, the right advice is sometimes to leave it alone',
      ],
      note:
        'We will tell you if you do not need sedation. Paying for it when local anaesthetic alone would have been fine is not an outcome we are interested in.',
    },
    day: {
      heading: 'Your extraction day, step by step',
      summary: 'What actually happens, from the x-ray to the sofa.',
      steps: [
        {
          title: 'Assessment and x-rays',
          body: 'A shorter appointment first. We x-ray the teeth, look at the roots and the nerve, check your medical history and medication, and tell you honestly how straightforward or awkward the extraction is likely to be.',
        },
        {
          title: 'The plan and the quote',
          body: 'You leave knowing which teeth are coming out, in how many appointments, how long we expect to need, and the total cost in writing. Then you decide in your own time.',
        },
        {
          title: 'Arrive with your escort',
          body: 'A responsible adult over 18 comes with you or meets you here. After wisdom teeth you will be both sedated and sore, so having someone to take you home matters more than usual.',
        },
        {
          title: 'Cannula, then sedation',
          body: 'A fine cannula goes into your hand or arm, the sedative follows, and it takes effect within a minute or two. Local anaesthetic is given once you are relaxed.',
        },
        {
          title: 'The extraction, monitored throughout',
          body: 'The teeth are removed while you are continuously monitored. Most patients are aware of very little of this and remember less.',
        },
        {
          title: 'Recovery and the rest of the day',
          body: 'Twenty to thirty minutes resting with us, written aftercare instructions and a number to call, then home. Soreness and some swelling for a few days is normal; no driving, work or alcohol for the rest of the day.',
        },
      ],
    },
    safety: {
      heading: 'Who gives the sedation, and how you are kept safe',
      summary: 'Monitoring, emergency cover and who is actually in the room.',
      points: [
        {
          title: 'The same person plans and sedates',
          body: `${SEDATIONIST} is qualified in intravenous conscious sedation and carries out the extraction, so the person judging how long you need under sedation is the person doing the surgery.`,
        },
        {
          title: 'A second trained pair of eyes',
          body: 'A trained team member monitors your oxygen levels, pulse and blood pressure for the whole appointment, and is not assisting with the surgery while doing it.',
        },
        {
          title: 'Imaging before anything is touched',
          body: 'Surgical wisdom teeth are planned from x-rays, so the roots, the sinus and the nerve are known quantities before the appointment rather than discoveries during it.',
        },
        {
          title: 'Emergency drugs and equipment on site',
          body: 'Reversal drugs and emergency equipment are kept here, checked, and the team practises the protocols for using them.',
        },
        {
          title: 'Aftercare you can reach',
          body: 'You go home with written instructions and a number to call if the bleeding, swelling or pain is not behaving as described.',
        },
      ],
    },
    fees: {
      heading: 'What wisdom teeth under sedation costs',
      intro:
        'Two things are being priced: the sedation, charged by the hour, and the extraction itself, which depends on whether the tooth is simple or surgical. They are combined into one fixed written quote after the assessment.',
      rows: feeRows(['Wisdom tooth extraction', 'quoted separately', 'Simple and surgical removals are priced differently']),
    },
    local: {
      heading: 'Wisdom teeth patients travel to Acton from across West London',
      intro:
        'Wisdom teeth tend to flare up at the worst possible time, and a hospital referral can mean months of waiting. Patients come to us in Acton W3 from across West London to have it dealt with sooner, and under sedation if they want it.',
    },
    gettingHome: {
      heading: 'Getting home after an extraction',
      intro:
        'Plan this before the appointment, not on the day. You will be recovering from sedation and from surgery at the same time, and you cannot drive.',
    },
    related: [
      { to: '/iv-sedation-dentistry', label: 'IV sedation — the full picture', body: 'How sedation works, safety, fees and what it feels like.' },
      { to: '/iv-sedation-dental-phobia', label: 'Frightened of the dentist?', body: 'If avoidance is the reason the wisdom teeth got this bad.' },
      { to: '/iv-sedation-dental-implants', label: 'Implants under sedation', body: 'Planned surgery, relaxed appointment.' },
      { to: '/services/sedation', label: 'Sedation options explained', body: 'How IV sedation compares with the alternatives.' },
      { to: '/emergency-dentist-acton', label: 'Emergency dentist Acton', body: 'Swelling or pain right now? Call us today.' },
      { to: '/knowledge-base/a-guide-to-iv-sedation-vs-general-anaesthetic', label: 'Guide: sedation vs general anaesthetic', body: 'Why hospital is rarely needed for wisdom teeth.' },
      { to: '/pricing', label: 'Full fee guide', body: 'Every treatment price in one place.' },
    ],
    enquiryIntro:
      'Tell us roughly what is going on and we will call you back. This is not a screening test and it does not give you a verdict — it just means the conversation starts somewhere useful.',
    faqs: [
      {
        question: 'How much does it cost to have wisdom teeth out under sedation?',
        answer:
          'Sedation is from £450 for the first hour and from £250 for each additional hour, added to the extraction fee, which depends on whether the tooth is simple or surgical. The assessment is from £75. You get one fixed written quote covering everything before you commit to anything.',
      },
      {
        question: 'Is it safe to have all four wisdom teeth removed in one appointment?',
        answer:
          'For many patients yes, and sedation is what makes it tolerable. Whether it is right for you depends on the x-rays, the position of the roots and your medical history. If the teeth are better taken out across two visits, we will say so at the assessment.',
      },
      {
        question: 'Will I remember the extraction?',
        answer:
          'Most patients remember the cannula going in, then waking up in recovery, with the surgery itself hazy or missing. We cannot promise a complete blank and some people recall fragments. What nearly everyone reports is that it was not the ordeal they had braced for.',
      },
      {
        question: 'Can I drive home after wisdom teeth under sedation?',
        answer:
          'No. Driving is out for the rest of the day, so leave the car at home. Your escort takes you home, ideally by taxi or a short train journey. Most people are fit to drive again the next morning after a full night of sleep.',
      },
      {
        question: 'Do I need someone to come with me?',
        answer:
          'Yes, a responsible adult over 18. After wisdom teeth you are recovering from sedation and from surgery at once, so having someone to collect painkillers, make tea and keep an eye on you for the evening matters. We cannot proceed if you arrive alone.',
      },
      {
        question: 'How long will the appointment take?',
        answer:
          'Expect to be with us around two hours in total: settling in, the extraction, then twenty to thirty minutes of monitored recovery. One straightforward tooth can be quick. Four impacted ones take considerably longer, and the quote says so in advance.',
      },
      {
        question: 'Is sedation the same as being knocked out for wisdom teeth?',
        answer:
          'No. A general anaesthetic for wisdom teeth means hospital and full unconsciousness. IV sedation keeps you awake and breathing for yourself, but relaxed enough that the surgery passes you by. For most straightforward and surgical extractions, sedation is enough.',
      },
      {
        question: 'What if I take regular medication or have a medical condition?',
        answer:
          'Bring a full list, including inhalers and anything bought over the counter. Blood thinners, heart and lung conditions and several other medicines change either the sedation or the extraction plan. All of it is reviewed at the assessment, never improvised on the day.',
      },
    ],
  },

  // ==========================================================================
  // IMPLANTS
  // ==========================================================================
  implants: {
    key: 'implants',
    path: '/iv-sedation-dental-implants',
    eyebrow: 'Acton W3 · implant surgery under sedation',
    h1: 'Dental Implants Under IV Sedation — Acton',
    title: 'Dental Implants Under Sedation | Acton W3',
    description:
      'Implant surgery under IV sedation in Acton W3. Place several implants in one relaxed appointment. Sedation from £450 plus implant fees. Adult escort required.',
    keywords: [
      'implants under sedation',
      'sedation for implant surgery',
      'dental implants under sedation London',
      'IV sedation implants Acton',
      'nervous patient dental implants West London',
    ],
    image: {
      src: 'https://images.pexels.com/photos/36061625/pexels-photo-36061625.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      alt: 'A relaxed patient sitting in soft daylight after treatment',
    },
    directAnswer: {
      question: 'Can implant surgery be done under sedation?',
      answer:
        'Yes. Implant placement is planned, predictable surgery, which makes it one of the best fits for IV sedation. You stay awake but deeply relaxed and usually remember little of it. At our Acton W3 practice sedation is from £450 on top of the implant fees, with an adult escort and an in-person assessment required.',
      bullets: [
        'Several implants placed in one relaxed appointment',
        'Sedation from £450, plus the implant fees',
        `Given by ${SEDATIONIST}`,
        'CBCT-planned surgery · 0% finance available',
      ],
    },
    feels: {
      heading: 'What implant surgery feels like under sedation',
      intro:
        'Implant surgery is far less dramatic than it sounds — but "less dramatic than it sounds" is not much comfort if the thought of it keeps you awake. Here is what sedation changes.',
      points: [
        {
          title: 'Pressure and sound, not pain',
          body: 'Implant placement is felt as pressure and vibration rather than pain, because the area is fully numb. Sedation deals with the other half of the problem: knowing it is happening.',
        },
        {
          title: 'The length stops mattering',
          body: 'Implant appointments are longer than most dental visits. Sedated patients consistently say the time collapsed — an hour and a half felt like ten minutes.',
        },
        {
          title: 'You can still cooperate',
          body: 'You stay awake and responsive, which matters during surgery: you can open, turn your head and follow a simple instruction, while remaining entirely uninterested in the detail.',
        },
        {
          title: 'Usually little memory of the surgery',
          body: 'Most patients remember the cannula, then recovery, with the middle hazy. We will not promise a blank — only that what people recall is rarely what they feared.',
        },
      ],
      reassure:
        'If a previous dental experience is the reason you have put up with a gap or a failing bridge for years, say that when you get in touch. It changes how we plan the appointment.',
    },
    oneAppointment: {
      heading: 'What can be done in one sedated appointment',
      intro:
        'Sedation is at its most useful when several surgical steps can be combined, so you go through the recovery once instead of three times.',
      items: [
        'Several implants placed in one visit, in one or both jaws',
        'An extraction and an implant in the same appointment, where the site allows it',
        'Bone grafting or a sinus procedure alongside implant placement',
        'Full-arch work, where multiple implants support a whole set of teeth',
        'Implant placement combined with other treatment you have been putting off',
      ],
      note:
        'What is possible depends on the CBCT scan, the bone available and your medical history. The plan, the expected length of the appointment and the total cost are all agreed in writing before the date is booked.',
    },
    suits: {
      heading: 'When sedation makes sense for implants — and when it does not',
      yes: [
        'Patients who want implants but cannot face surgery while fully aware',
        'Several implants, grafting, or a long appointment in one sitting',
        'A strong gag reflex, or difficulty staying still for an hour or more',
        'Needle phobia, where the local anaesthetic itself is the barrier',
        'Patients returning after a bad previous surgical experience elsewhere',
      ],
      caution: [
        'A single straightforward implant is often more comfortable than patients expect with local anaesthetic alone',
        'Some medical and medication histories mean sedation is not the safer choice',
        'Bisphosphonates, uncontrolled diabetes and smoking affect implant planning as well as sedation',
        'We do not sedate under-18s',
        'Where sedation is not suitable, we discuss the alternatives or refer you on',
      ],
      note:
        'Sedation does not change the surgery or the success rate. It changes your experience of the appointment, which for some patients is the difference between having the treatment and not having it.',
    },
    day: {
      heading: 'Your implant surgery day, step by step',
      summary: 'From the CBCT scan to the end of the afternoon.',
      steps: [
        {
          title: 'Assessment, scan and plan',
          body: 'A CBCT scan shows the bone in three dimensions, so the implant position is planned to the millimetre before the appointment. Your medical history and medication are reviewed at the same visit.',
        },
        {
          title: 'The written quote',
          body: 'You leave with the plan, the expected number of appointments, how long the surgery should take, and the total cost in writing — sedation and treatment together, with finance options if you want them.',
        },
        {
          title: 'Arrive with your escort',
          body: 'A responsible adult over 18 brings you or meets you here. Implant appointments run long, so it is worth agreeing in advance whether they will wait or come back.',
        },
        {
          title: 'Cannula, then sedation',
          body: 'A fine cannula goes into your hand or arm and the sedative follows, taking effect within a minute or two. Local anaesthetic is given once you are relaxed.',
        },
        {
          title: 'Surgery, monitored throughout',
          body: 'The implants are placed to the plan while you are continuously monitored. Most patients are barely aware of this part and remember less of it.',
        },
        {
          title: 'Recovery, then healing',
          body: 'Twenty to thirty minutes with us, written aftercare and a number to call, then home with your escort. No driving or work for the rest of the day. The implant then integrates over the following months.',
        },
      ],
    },
    safety: {
      heading: 'Who gives the sedation, and how you are kept safe',
      summary: 'Planning, monitoring and emergency cover for a longer surgical appointment.',
      points: [
        {
          title: 'One clinician, plan and sedation',
          body: `${SEDATIONIST} is qualified in intravenous conscious sedation and places the implants, so the sedation is judged by the person who knows exactly how long the surgery will take.`,
        },
        {
          title: 'Monitored for the whole appointment',
          body: 'Your oxygen levels, pulse and blood pressure are watched continuously by a trained team member — important in a longer appointment, not just a short one.',
        },
        {
          title: 'Surgery planned from a 3D scan',
          body: 'CBCT planning means the nerve, the sinus and the available bone are known before we start, so sedated time is spent placing implants rather than working things out.',
        },
        {
          title: 'Emergency drugs and equipment on site',
          body: 'Reversal drugs and emergency equipment are kept here, checked, and the team rehearses the protocols for using them.',
        },
        {
          title: 'Reviewed in advance',
          body: 'Medication that affects bone healing, bleeding or sedation is picked up at the assessment, well before a surgical date is offered.',
        },
      ],
    },
    fees: {
      heading: 'What implants under sedation costs',
      intro:
        'Sedation is charged by the hour and added to the implant fees — a single implant with abutment and crown is from £2,295. Both appear in one fixed written quote after the assessment, and 0% finance is available over £500.',
      rows: feeRows(['Implant treatment', 'from £2,295', 'Single implant with abutment and crown; see the fee guide']),
    },
    local: {
      heading: 'Implant patients travel to Acton from across West London',
      intro:
        'Implants are a considered purchase, and patients research them properly before travelling. Ours come from across West London to Acton W3 — often because they want one clinician doing the planning, the surgery and the sedation rather than three.',
    },
    gettingHome: {
      heading: 'Getting home after implant surgery',
      intro:
        'Implant appointments are long, so the journey home needs a little more thought than usual — and you cannot drive yourself.',
    },
    related: [
      { to: '/iv-sedation-dentistry', label: 'IV sedation — the full picture', body: 'How it works, safety, fees and what it feels like.' },
      { to: '/implants-acton', label: 'Dental implants in Acton', body: 'Costs, timings and the full implant process.' },
      { to: '/iv-sedation-wisdom-teeth-removal', label: 'Wisdom teeth under sedation', body: 'Surgical extractions in one relaxed appointment.' },
      { to: '/iv-sedation-dental-phobia', label: 'Frightened of the dentist?', body: 'Where to start after years of avoiding it.' },
      { to: '/services/all-on-4-implants', label: 'All-on-4 full arch', body: 'A whole arch supported on four implants.' },
      { to: '/knowledge-base/a-guide-to-what-iv-sedation-really-costs', label: 'Guide: what sedation really costs', body: 'How sedation is priced on top of the implant fees.' },
      { to: '/finance', label: '0% dental finance', body: 'Spread sedation and implant fees monthly.' },
    ],
    enquiryIntro:
      'Tell us what you are missing and how you feel about surgery. There is no verdict at the end of this form — it just means the call back starts from something useful.',
    faqs: [
      {
        question: 'How much do implants under sedation cost?',
        answer:
          'Sedation is from £450 for the first hour and from £250 per additional hour, on top of the implant fees — a single implant with abutment and crown is from £2,295. The assessment is from £75. Everything goes into one fixed written quote, and 0% finance is available.',
      },
      {
        question: 'Is sedation safe for implant surgery?',
        answer:
          'Implant placement is planned from a CBCT scan before anything is touched, and the sedation is assessed, given and monitored separately. Your oxygen, pulse and blood pressure are watched throughout by a trained team member, and reversal drugs are kept on site and checked.',
      },
      {
        question: 'Will I remember the implant surgery?',
        answer:
          'Most patients remember the cannula and then recovery. Implant surgery is felt as pressure and sound rather than pain, and those are exactly the things sedation makes unimportant. We cannot promise you will remember nothing, only that it is usually hazy at worst.',
      },
      {
        question: 'Can I drive after implant surgery under sedation?',
        answer:
          'No. Implant surgery alone would not stop you driving, but the sedation does, for the rest of the day. Arrange a lift or a taxi and plan nothing else that afternoon. You will usually be fine to drive again the following morning.',
      },
      {
        question: 'Do I need someone with me?',
        answer:
          'Yes, a responsible adult over 18 must bring you or meet you here and take you home. Implant appointments run longer than most, so warn them they may be waiting a while, or agree a time for them to come back for you.',
      },
      {
        question: 'How long does an implant appointment under sedation take?',
        answer:
          'Longer than most dental visits, which is why sedation is charged by the hour. A single implant may take about an hour; several implants, grafting or extractions at the same time can take two or three. The expected time is stated in your quote.',
      },
      {
        question: 'Is this the same as having implants under general anaesthetic?',
        answer:
          'No. A general anaesthetic means hospital admission and full unconsciousness. IV sedation is given here at the practice: you stay awake, breathing for yourself and able to respond, but deeply relaxed. Almost all implant surgery can be done comfortably this way.',
      },
      {
        question: 'What if I take regular medication?',
        answer:
          'Bring everything you take. Bisphosphonates, blood thinners, diabetes medication and some other drugs affect healing, surgery or sedation, and smoking affects implant success. All of it is reviewed at the assessment before any surgical date is offered.',
      },
    ],
  },

  // ==========================================================================
  // DENTAL PHOBIA
  // ==========================================================================
  phobia: {
    key: 'phobia',
    path: '/iv-sedation-dental-phobia',
    eyebrow: 'Acton W3 · nervous and phobic patients',
    h1: 'Frightened of the Dentist? Dental Phobia Help in Acton',
    title: 'Dentist for Nervous Patients Acton W3 | Restore.Dental',
    description:
      'Dental phobia help in Acton W3. Avoided the dentist for years? Start with a conversation. IV sedation from £450 if you need it. No lectures, no judgement.',
    keywords: [
      'dentist for nervous patients',
      'dental phobia help',
      'scared of the dentist',
      'dental phobia dentist Acton',
      'nervous patient dentist West London',
      'sedation dentist for anxiety London',
    ],
    image: {
      src: 'https://images.pexels.com/photos/6764091/pexels-photo-6764091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      alt: 'Two people sitting together, one offering quiet reassurance',
    },
    directAnswer: {
      question: 'I am frightened of the dentist — where do I start?',
      answer:
        'Start with a phone call or an enquiry, not an appointment. We talk first, then book a short assessment where nothing is done to you on the day. If treatment still feels impossible while you are aware of it, IV sedation is available at our Acton W3 practice from £450, with an adult escort.',
      bullets: [
        'A conversation first — nothing done on the first visit',
        'IV sedation from £450 if you decide you want it',
        'Years away is normal here, and nobody comments on it',
        'Everything explained, nothing started without your say',
      ],
    },
    feels: {
      heading: 'What we already know about you, before you call',
      intro:
        'Patients who have stayed away for years arrive expecting a telling-off and a lecture about flossing. That is not what happens, and it is worth saying so plainly.',
      points: [
        {
          title: 'Nobody will comment on how long it has been',
          body: 'Eight years, eighteen years, since school — it makes no clinical difference to what happens next, and it is not interesting to us. What matters is where you are now and what you want to change.',
        },
        {
          title: 'You can stop at any point',
          body: 'Raise a hand and everything stops. That is not a nice line on a website; it is the mechanism that makes the appointment survivable, and we use it as often as patients need.',
        },
        {
          title: 'Nothing is done without warning you first',
          body: 'No surprise instrument, no "just a quick one while we are here". You are told what is next, and you agree to it, before it happens.',
        },
        {
          title: 'Sedation is an option, not the plan',
          body: 'Plenty of anxious patients never need it. Some do, and that is fine too. With IV sedation you stay awake but deeply relaxed, and most people remember very little of the appointment.',
        },
      ],
      reassure:
        'If reading this far has been hard, you do not have to book anything today. Send an enquiry and ask us to call — you can say "I am terrified, please explain it to me" and that is a perfectly normal start.',
    },
    oneAppointment: {
      heading: 'Everything in one go, instead of coming back six times',
      intro:
        'For phobic patients the hardest part is often not the treatment — it is the dread between appointments. Sedation lets us compress months of visits into one.',
      items: [
        'A check-up, x-rays and the first stage of treatment in a single visit',
        'Several fillings in one sitting instead of one tooth at a time',
        'A deep clean and gum treatment you could not otherwise sit through',
        'An extraction you have been dreading, done and behind you',
        'A front tooth repaired, so you can get back to smiling in photographs',
      ],
      note:
        'What can be combined depends on your mouth, your medical history and how long you can safely be sedated. We plan it with you beforehand so there are no surprises and no open-ended appointments.',
    },
    suits: {
      heading: 'Who sedation helps, and when it is not the answer',
      yes: [
        'Patients who have avoided dentistry for years and cannot face starting',
        'Anyone who has walked out of an appointment, or could not get through one',
        'Needle phobia, including patients who faint or feel faint',
        'A gag reflex that makes even x-rays difficult',
        'Patients with a traumatic dental experience behind them, often from childhood',
      ],
      caution: [
        'Some medical and medication histories mean sedation is not the safer choice',
        'Pregnancy and breastfeeding need separate advice',
        'We do not sedate under-18s',
        'For many anxious patients, time, explanation and a gentler pace work without any sedation at all',
        'Where sedation is not suitable, we talk through the alternatives or refer you to a service that can help',
      ],
      note:
        'We will not sell you sedation you do not need. Quite often the first appointment proves it was the unknown rather than the dentistry that was frightening, and the second one needs nothing extra.',
    },
    day: {
      heading: 'How the first appointment actually goes',
      summary: 'Open this if the unknown is the worst part. Step by step, nothing dramatic.',
      steps: [
        {
          title: 'A phone call, before anything else',
          body: 'You can ask every question before you book. Tell us what you are dreading and what has gone wrong before — it is the most useful thing we can know, and it changes how the visit is planned.',
        },
        {
          title: 'The first visit: talking and looking',
          body: 'A short appointment. We talk, we look, we may take x-rays if you are ready. No treatment is started on the day unless you ask for it. You can keep your coat on and sit up.',
        },
        {
          title: 'A plan you can read at home',
          body: 'You leave with what we found, what we recommend, what it costs in writing, and what would happen if you did nothing. No pressure to book anything there and then.',
        },
        {
          title: 'If you choose sedation: the assessment',
          body: 'A separate appointment to go through your medical history and medication, explain what sedation will and will not do, and agree the treatment plan and the escort arrangements.',
        },
        {
          title: 'The sedation appointment',
          body: 'A fine cannula into the hand or arm, the sedative, and then treatment while you are continuously monitored. You stay awake and able to respond, but deeply relaxed and largely uninterested.',
        },
        {
          title: 'Recovery and afterwards',
          body: 'Twenty to thirty minutes with us, then home with your escort. No driving or work for the rest of the day. Most patients tell us the hardest part was making the first phone call.',
        },
      ],
    },
    safety: {
      heading: 'Who gives the sedation, and how you are kept safe',
      summary: 'For the people who need to know the detail before they can relax.',
      points: [
        {
          title: 'A qualified sedationist',
          body: `Your sedation is given by ${SEDATIONIST}, qualified in intravenous conscious sedation, with the practice team trained to assist and monitor throughout the appointment.`,
        },
        {
          title: 'Someone watching you, not the treatment',
          body: 'A trained team member monitors your oxygen levels, pulse and blood pressure for the whole appointment. During that time, you are what they are responsible for.',
        },
        {
          title: 'Emergency drugs and protocols',
          body: 'Reversal drugs and emergency equipment are kept on site and checked, and the team rehearses the protocols for using them.',
        },
        {
          title: 'Your history reviewed in advance',
          body: 'Medication, medical conditions and any previous reaction to sedation are gone through at the assessment beforehand, so nothing has to be worked out while you are in the chair.',
        },
        {
          title: 'Registered and regulated',
          body: 'Every clinician here is registered with the General Dental Council, and you are welcome to check the register before you come anywhere near us.',
        },
      ],
    },
    fees: {
      heading: 'What it costs',
      intro:
        'Talking to us costs nothing. From there, the assessment and the sedation are priced separately from the treatment, and all of it arrives as one fixed written quote — so there is nothing to work out while you are feeling anxious.',
      rows: feeRows(['The treatment itself', 'quoted separately', 'Nothing is started before you have the quote in writing']),
    },
    local: {
      heading: 'Nervous patients travel to Acton from across West London',
      intro:
        'Anxious patients rarely choose the nearest practice. They choose the one that sounds like it will not make things worse. Ours come to Acton W3 from across West London, usually on the recommendation of somebody who was dreading it too.',
    },
    gettingHome: {
      heading: 'Getting here, and getting home',
      intro:
        'Worth sorting out in advance, because the practical worries are often what tips an anxious patient into cancelling.',
    },
    related: [
      { to: '/iv-sedation-dentistry', label: 'IV sedation — the full picture', body: 'How it works, safety, fees and what it feels like.' },
      { to: '/iv-sedation-wisdom-teeth-removal', label: 'Wisdom teeth under sedation', body: 'The extraction you have been putting off.' },
      { to: '/iv-sedation-dental-implants', label: 'Implants under sedation', body: 'Surgery you would rather not be aware of.' },
      { to: '/services/sedation', label: 'Sedation options explained', body: 'How IV sedation compares with the alternatives.' },
      { to: '/dentist-acton', label: 'General dentist in Acton', body: 'Check-ups, hygiene and ordinary dentistry.' },
      { to: '/knowledge-base/a-guide-to-what-iv-sedation-feels-like', label: 'Guide: what sedation feels like', body: 'The unknown is the worst part. This is the whole of it.' },
      { to: '/reviews', label: 'What patients say', body: 'Reviews from people who were dreading it too.' },
    ],
    enquiryIntro:
      'Six questions, no verdict at the end. It will not tell you whether you are suitable — it means whoever calls you back already knows the parts that are hard to say out loud.',
    faqs: [
      {
        question: 'How much does sedation for dental anxiety cost?',
        answer:
          'Talking to us costs nothing. A sedation assessment is from £75, and IV sedation is from £450 for the first hour with £250 for each additional hour, plus the treatment itself. Nothing is charged, and nothing is started, before you have a written quote.',
      },
      {
        question: 'Is sedation safe for a very anxious patient?',
        answer:
          'Sedation for anxious patients follows the same routine as any other: medical history screened in advance, continuous monitoring of oxygen, pulse and blood pressure, reversal drugs on site, and a clinician whose responsibility during your appointment is watching you rather than the treatment.',
      },
      {
        question: 'Will I remember anything?',
        answer:
          'Usually very little, which for most phobic patients is the point. Being aware of what is happening and feeling unable to leave is the part sedation tends to remove. We will not promise a blank; we will say it is usually nothing like you are imagining.',
      },
      {
        question: 'Can I drive afterwards?',
        answer:
          'No driving for the rest of the day, and no work either. Many patients take the day off and treat it as the day they finally dealt with it. You will usually feel like yourself again the following morning.',
      },
      {
        question: 'Do I need someone to come with me?',
        answer:
          'Yes, someone over 18 has to bring you or meet you here and take you home. For phobic patients that person is often the reason the appointment happens at all, so bring whoever keeps you calm — they can sit with you until you are sedated.',
      },
      {
        question: 'How long does the sedation last?',
        answer:
          'The sedative works within a minute or two, we keep you comfortable for the length of the appointment, and you recover with us for twenty to thirty minutes. The grogginess fades across the day. The relief, patients tell us, lasts rather longer than that.',
      },
      {
        question: 'Is this being put to sleep?',
        answer:
          'No. Being put to sleep completely means hospital and a general anaesthetic. IV sedation is given here, keeps you awake and breathing for yourself, and leaves you relaxed and uninterested rather than unconscious. For dental phobia it is usually all that is needed.',
      },
      {
        question: 'What if I take medication for anxiety or depression?',
        answer:
          'Tell us about everything, including antidepressants, anxiety medication, inhalers and anything bought over the counter. None of it is judged and most of it is workable — it simply has to be known in advance so the plan is safe for you.',
      },
    ],
  },
};

export const getSedationPage = (key) => sedationPages[key];
